import { HTMLAttributes, SyntheticEvent } from 'react';

import { Dayjs } from 'dayjs';
import { FunctionResponse } from 'generated';
import { matchSorter } from 'match-sorter';
import { ControllerFieldState, ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { queries } from 'RestAPIs/rapini';

import { Grid, TextField, TextFieldProps } from '@mui/material';

import { VirtualizedAutocomplete } from 'components/shared';

type Props = TextFieldProps & {
  field?: ControllerRenderProps<any, string>;
  fieldState?: ControllerFieldState;
  fullWidth?: boolean;
  label: string;
  onChange?: (
    event: SyntheticEvent<Element, Event>,
    func?: FunctionResponse | FunctionResponse[]
  ) => void;
  required?: boolean;
  value?: number;
  startDate: Dayjs;
  endDate: Dayjs;
  onlyPreferred: boolean;
  organizationalUnitId: number;
  onlySelectable: boolean;
};

export type Option = {
  id?: number;
  value?: number;
  label: string;
  description?: string;
  code?: string;
};

const FunctionSelector = (props: Props) => {
  const {
    field,
    fieldState,
    fullWidth,
    label,
    required,
    startDate,
    endDate,
    onlyPreferred,
    organizationalUnitId,
    onlySelectable,
    variant = 'outlined'
  } = props;
  const { t } = useTranslation('common');

  const renderOption = (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: Option
  ) => {
    return (
      <li {...props}>
        <Grid container spacing={2}>
          <Grid className="code" item xs={3}>
            {option.code}
          </Grid>
          <Grid className="description" item xs={9}>
            {option.description}
          </Grid>
        </Grid>
      </li>
    );
  };

  const { data: functions } = queries.useGetFunctions(
    startDate.format('YYYY-MM-DD'),
    endDate.format('YYYY-MM-DD'),
    onlyPreferred,
    organizationalUnitId,
    onlySelectable,
    {
      enabled: startDate.isValid() && endDate.isValid()
    }
  );

  const options = ((functions ?? []) as FunctionResponse[]).map((func) => ({
    label: func.description || t('noDescription') || '-',
    description: func.description,
    code: func.code,
    id: func.id,
    value: func.id
  }));

  const filterOptions = (options: Option[], { inputValue }) =>
    matchSorter(options, inputValue, { keys: ['code', 'description'] });

  const getFunctionLabel = (option: string | Option | number) => {
    if (typeof option === 'string') {
      return option;
    }
    if (typeof option === 'number') {
      return options.find((o) => o.id === option)?.description || '';
    }
    return option?.description || '';
  };

  return (
    <VirtualizedAutocomplete
      options={options}
      getOptionLabel={(option) => getFunctionLabel(option)}
      value={options.find((o) => o.id === field?.value) || null}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          fullWidth={fullWidth}
          error={!!fieldState?.error?.type}
          helperText={fieldState?.error?.message}
          variant={variant}
          size="small"
        />
      )}
      renderOption={renderOption}
      filterOptions={filterOptions}
      onChange={(event, value) => {
        const values = Array.isArray(value)
          ? value.filter((v) => v)
          : value
          ? [value]
          : [];
        const v = values.length ? values[0] : null;
        if (typeof v === 'string') {
          field?.onChange(null);
        } else {
          field?.onChange(v?.id || null);
        }
      }}
    />
  );
};

export default FunctionSelector;
